import { makeAutoObservable } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import { type IRcsComplianceCreateParams, type IResponseRcsCompliance } from 'entities/Rcs'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'
import { BrandContactsStore } from './brandContactsStore'
import { BrandInfoStore } from './brandInfoStore'
import { BrandMediaStore } from './brandMediaStore'
import { BrandTermsStore } from './brandTermsStore'
import { type IRcsPreviewTabs } from './type'

export class SenderDetailsStore {
  private _activeTab: IRcsPreviewTabs = 'profile'
  readonly brandInfoStore: BrandInfoStore
  readonly brandMediaStore: BrandMediaStore
  readonly brandContactsStore: BrandContactsStore
  readonly brandTermsStore: BrandTermsStore

  constructor(wizardStore: RcsWizardStore) {
    this.brandInfoStore = new BrandInfoStore(wizardStore)
    this.brandMediaStore = new BrandMediaStore()
    this.brandContactsStore = new BrandContactsStore()
    this.brandTermsStore = new BrandTermsStore()

    makeAutoObservable(this)
  }

  get activeTab() {
    return this._activeTab
  }

  setActiveTab = (tab: IRcsPreviewTabs) => {
    this._activeTab = tab
  }

  get previewTabs(): { label: string; value: IRcsPreviewTabs }[] {
    return [
      {
        label: 'Business info',
        value: 'profile',
      },
      {
        label: 'Conversation',
        value: 'conversation',
      },
    ]
  }

  get isNextDisabled() {
    return !(
      this.brandInfoStore.isValid &&
      this.brandMediaStore.isValid &&
      this.brandContactsStore.isValid &&
      this.brandTermsStore.isValid
    )
  }

  get params(): IRcsComplianceCreateParams {
    const { brandName, brandDescription, messageType } = this.brandInfoStore
    const { logoUrl, accentColor, bannerUrl } = this.brandMediaStore
    const { privacyPolicyUrl, termsAndConditionsUrl } = this.brandTermsStore
    const { numbers, websites, emails, hasNumbers, hasWebsites, hasEmails } =
      this.brandContactsStore

    const params: IRcsComplianceCreateParams = {
      message_type: messageType,
      name: brandName,
      description: brandDescription,
      logo_path: logoUrl,
      accent_color: accentColor,
      banner_path: bannerUrl,
      privacy_policy_url: privacyPolicyUrl,
      terms_url: termsAndConditionsUrl,
    }

    if (hasNumbers) {
      params.contact_phones = numbers.map((number) => ({
        phone_number: number.phoneInputStore.fullUnformattedNumber,
        label: number.label,
        country_code: String(number.phoneInputStore.country.id),
      }))
    }

    if (hasEmails) {
      params.contact_emails = emails.map((email) => ({
        email: email.email,
        label: email.label,
      }))
    }

    if (hasWebsites) {
      params.website_urls = websites.map((website) => ({
        website: website.url,
        label: website.label,
      }))
    }

    return params
  }

  validate = () => {
    return [
      this.brandInfoStore.validate(),
      this.brandMediaStore.validate(),
      this.brandContactsStore.validate(),
      this.brandTermsStore.validate(),
    ].some(Boolean)
  }

  init = (data: IResponseRcsCompliance) => {
    this.brandInfoStore.init(data)
    this.brandMediaStore.init(data)
    this.brandContactsStore.init(data)
    this.brandTermsStore.init(data)
  }

  initErrors = (errors: INormalizedApiError) => {
    this.brandInfoStore.initErrors(errors)
    this.brandMediaStore.initErrors(errors)
    this.brandContactsStore.initErrors(errors)
    this.brandTermsStore.initErrors(errors)
  }

  reset = () => {
    this._activeTab = 'profile'
    this.brandInfoStore.reset()
    this.brandMediaStore.reset()
    this.brandContactsStore.reset()
    this.brandTermsStore.reset()
  }

  dispose = () => {
    this.brandMediaStore.dispose()
  }
}
